import { DataSource, StatsigClient, StatsigUpdateDetails } from '@statsig/js-client';
import { getLanguageFromPath, getBasePathWithoutLanguage } from 'src/utils/i18n-config';

// The client exists once the visitor has granted Performance cookies, or, in a
// region whose consent model is not opt-in, as soon as OneTrust has loaded
// (AbTestingProvider). A missing client is the normal state before either.
// `awaiting_consent` is a visitor whose assignment is not yet known: a
// returning one whose earlier answer OneTrust has not yet reported, or a
// first-time one whose consent model it has not yet reported. Experiments stay
// loading until it does, or until the provider's timeout settles them.
// `unknown` is the state before the provider has run: a component that mounts
// first must wait for it rather than read the absence of a client as settled.
type ClientStatus = 'unknown' | 'no_client' | 'awaiting_consent' | 'initializing' | 'initialized';

export type StatsigSnapshot = {
  status: ClientStatus;
  client: StatsigClient | null;
  // Where an initialized client's values came from; undefined when initialize rejected.
  source?: DataSource;
};

type DataLayerEntry = Record<string, unknown>;

let statsigClientInstance: StatsigClient | null = null;
let clientStatus: ClientStatus = 'unknown';
let snapshot: StatsigSnapshot = { status: clientStatus, client: null };
const listeners = new Set<() => void>();

// Each transition replaces the snapshot, so `useSyncExternalStore` sees a new
// object only when something changed.
const setStatus = (status: ClientStatus, source?: DataSource): void => {
  clientStatus = status;
  snapshot = { status, client: statsigClientInstance, source };
  listeners.forEach((listener) => listener());
};

export const subscribeStatsig = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getStatsigSnapshot = (): StatsigSnapshot => snapshot;

// A client holds its events when the browser sends Global Privacy Control, or
// once the visitor answers without Performance: the SDK keeps exposures and
// custom events on the device with logging disabled, and the GA4 bridge below
// keeps its dataLayer entries in memory. Both are released together when
// Performance is granted.
let eventsHeld = false;
let heldDataLayerEntries: DataLayerEntry[] = [];

// A page-level experiment holds the page at opacity 0 until the client settles,
// so every initialize is bounded: on the timeout the SDK resolves with whatever
// values it has (a returning visitor's cached assignment, or none), and
// experiments then resolve. Without it, a Statsig request that hangs rather
// than fails holds the page for the SDK's 10 s network timeout.
const CLIENT_INIT_TIMEOUT_MS = 2000;

export const setStatsigClient = (client: StatsigClient, { holdEvents = false } = {}): void => {
  statsigClientInstance = client;
  eventsHeld = holdEvents;
  heldDataLayerEntries = [];
  setStatus('initializing');

  // A client replaced before its initialize finished must not settle its successor.
  const settle = (source?: DataSource): void => {
    if (statsigClientInstance !== client) return;
    setStatus('initialized', source);
  };
  const report = (error: unknown): void => {
    if (typeof window !== 'undefined') {
      (window as any).TrackJS && (window as any).TrackJS.track(`<Statsig init error> ${error}`);
    }
  };
  // Every initialize settles, so experiments fall back to their defaults
  // instead of holding the page. The SDK resolves with `success: false` for
  // network failures, blocked requests and timeouts. Those are not reported
  // because they are routine for visitors who block Statsig. A rejection is
  // not expected from the SDK, so it is reported to TrackJS.
  const onResolved = (details?: StatsigUpdateDetails): void => {
    settle(details?.source);
  };
  const onRejected = (error: unknown): void => {
    settle();
    report(error);
  };
  client.initializeAsync({ timeoutMs: CLIENT_INIT_TIMEOUT_MS }).then(onResolved, onRejected);
};

export const getStatsigClient = (): StatsigClient => {
  if (!statsigClientInstance) {
    throw new Error('Statsig client not initialized');
  }
  return statsigClientInstance;
};

/** True while a client created before consent is holding its events. */
export const isStatsigHoldingEvents = (): boolean => eventsHeld;

/**
 * Lets a client created before consent send its events: the SDK flushes what
 * it held on the device, and the GA4 bridge pushes what it held in memory, in
 * order. A no-op for a client that was never holding.
 */
export const releaseStatsigEvents = (): void => {
  if (!statsigClientInstance || !eventsHeld) {
    return;
  }
  eventsHeld = false;
  statsigClientInstance.updateRuntimeOptions({ loggingEnabled: 'browser-only' });

  const entries = heldDataLayerEntries;
  heldDataLayerEntries = [];
  if (typeof window !== 'undefined' && window.dataLayer) {
    entries.forEach((entry) => window.dataLayer?.push(entry));
  }
};

/**
 * Holds the events of a client that was sending, on an opt-out: from here on
 * the SDK keeps them on the device and the GA4 bridge keeps them in memory, as
 * for a client created before consent. A no-op for a client already holding.
 */
export const holdStatsigEvents = (): void => {
  if (!statsigClientInstance || eventsHeld) {
    return;
  }
  eventsHeld = true;
  heldDataLayerEntries = [];
  statsigClientInstance.updateRuntimeOptions({ loggingEnabled: 'disabled' });
};

export const markStatsigAwaitingConsent = (): void => {
  if (clientStatus === 'unknown' || clientStatus === 'no_client') {
    setStatus('awaiting_consent');
  }
};

// Settles a pending consent decision as "no client"; a client that is already
// initializing or initialized is left alone.
export const markStatsigNoClient = (): void => {
  if (clientStatus === 'unknown' || clientStatus === 'awaiting_consent') {
    setStatus('no_client');
  }
};

const getPageMetadata = (): Record<string, string> => {
  if (typeof window === 'undefined' || !window.location) {
    return {};
  }

  const pathname = window.location.pathname;
  return {
    current_page_path: getBasePathWithoutLanguage(pathname),
    current_selected_language: getLanguageFromPath(pathname),
  };
};

// Bridge to GA4: every Statsig event is also pushed to the GTM dataLayer. GTM
// replays the whole dataLayer when it loads, so while events are held the
// entries wait in memory rather than reaching GA4 after a later consent.
const pushToDataLayer = (entry: DataLayerEntry): void => {
  if (eventsHeld) {
    heldDataLayerEntries.push(entry);
    return;
  }
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(entry);
  }
};

export const logStatsigEvent = (eventName: string, value?: string | number, metadata?: Record<string, any>): void => {
  if (!statsigClientInstance) {
    return;
  }

  const pageMetadata = getPageMetadata();
  const enrichedMetadata = { ...metadata, ...pageMetadata };

  statsigClientInstance.logEvent(eventName, value, enrichedMetadata);

  pushToDataLayer({
    event: eventName,
    ...(value !== undefined && { value }),
    ...enrichedMetadata,
  });
};

// Test helper - only use in tests to inject mock client
export const __setStatsigClientForTesting__ = (client: StatsigClient | null): void => {
  statsigClientInstance = client;
  eventsHeld = false;
  heldDataLayerEntries = [];
  setStatus(client ? 'initialized' : 'no_client', client ? 'Network' : undefined);
};

// Test helper - only use in tests to return to the state before the provider has run
export const __resetStatsigToUnknownForTesting__ = (): void => {
  statsigClientInstance = null;
  eventsHeld = false;
  heldDataLayerEntries = [];
  setStatus('unknown');
};
